import { useState, useRef, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { buildApiUrl } from "../../utils/apiConfig";
import { useAuth } from "../../contexts/AuthContext";
import { useAssistAccess } from "../../hooks/useAssistAccess";
import { fetchMyAssistUsage, type MyAssistUsage } from "../../services/polarisService";
import AssistAnswerBody from "./AssistAnswerBody";
import AssistSourceList from "./AssistSourceList";
import { parseAssistSources, type AssistSource } from "./assistSources";
import { estimateTokens, formatAssistTokenUsage } from "../../utils/tokenEstimate";
import ChatHistorySidebar, { type ConversationSummary } from "./ChatHistorySidebar";
import PolarisAssistIcon from "../../images/nav-icons/Polaris-Assist.svg";
import "../../styles/rag-chat.css";
import { apiClient } from "../../utils/apiClient";

const API_BASE = buildApiUrl("api/rag");
const SOFT_MAX_CHARS = 8000;
const MESSAGE_TOO_LONG = "Your message is too long. Please shorten it and try again.";
const GENERIC_CHAT_ERROR = "Something went wrong. Please try again.";

type Role = "user" | "assistant";

interface ChatMessage {
    role: Role;
    content: string;
    sources?: AssistSource[];
}

function priorUserContent(messages: ChatMessage[], index: number): string | undefined {
    for (let i = index - 1; i >= 0; i -= 1) {
        if (messages[i].role === "user") {
            return messages[i].content;
        }
    }
    return undefined;
}

interface PersistedMessage {
    role: Role;
    content: string;
    createdAt: string;
    sources?: AssistSource[] | null;
}

/** Split buffer on SSE event delimiter; Spring uses \n\n between events. */
function takeSseEvent(buffer: string): { frame: string; rest: string } | null {
    const idxLf = buffer.indexOf("\n\n");
    const idxCr = buffer.indexOf("\r\n\r\n");
    let cut: number;
    let skip: number;
    if (idxLf === -1 && idxCr === -1) return null;
    if (idxLf === -1) {
        cut = idxCr;
        skip = 4;
    } else if (idxCr === -1) {
        cut = idxLf;
        skip = 2;
    } else if (idxLf < idxCr) {
        cut = idxLf;
        skip = 2;
    } else {
        cut = idxCr;
        skip = 4;
    }
    return { frame: buffer.slice(0, cut), rest: buffer.slice(cut + skip) };
}

function extractSseDataPayload(line: string): string | null {
    if (!line.startsWith("data:")) return null;
    const value = line.slice(5);
    // SSE spec: optional single space after "data:" is a delimiter, not part of the payload.
    return value.startsWith(" ") ? value.slice(1) : value;
}

function parseStreamTokenPayload(raw: string): string {
    if (!raw) return raw;
    const trimmed = raw.trim();
    if (trimmed.startsWith('"')) {
        try {
            const parsed: unknown = JSON.parse(trimmed);
            if (typeof parsed === "string") return parsed;
        } catch {
            // Plain-text fallback for error frames and legacy servers.
        }
    }
    return raw;
}

function dispatchSseFrame(
    frame: string,
    onToken: (token: string) => void,
    onSources?: (sources: AssistSource[]) => void,
): void {
    const lines = frame.split(/\r?\n/);
    const payloads: string[] = [];
    let eventName = "message";
    for (const line of lines) {
        if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
            continue;
        }
        const payload = extractSseDataPayload(line);
        if (payload !== null) payloads.push(payload);
    }
    if (payloads.length === 0) return;
    const combined = payloads.join("\n");
    if (!combined || combined === "[DONE]") return;
    if (eventName === "sources") {
        try {
            onSources?.(parseAssistSources(JSON.parse(combined)));
        } catch {
            // Ignore a malformed sources frame so the answer still displays.
        }
        return;
    }
    onToken(parseStreamTokenPayload(combined));
}

async function streamChat(
    message: string,
    sessionId: string,
    onToken: (token: string) => void,
    onDone: () => void,
    onError: (err: string) => void,
    onSources?: (sources: AssistSource[]) => void,
): Promise<void> {
    try {
        const res = await apiClient(`${API_BASE}/chat`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                Accept: "text/event-stream",
                "Cache-Control": "no-store",
            },
            // Retrieval is global — no document category filter on chat requests.
            body: JSON.stringify({ message, sessionId }),
        });
        if (!res.ok) {
            let detail = GENERIC_CHAT_ERROR;
            try {
                const body = (await res.json()) as { detail?: string; message?: string };
                detail = body.detail ?? body.message ?? detail;
            } catch {
                // non-JSON error body
            }
            const error = new Error(detail) as Error & { status?: number };
            error.status = res.status;
            throw error;
        }
        if (!res.body) throw new Error("No response body");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        for (;;) {
            const { done, value } = await reader.read();
            buffer += decoder.decode(value ?? new Uint8Array(0), { stream: !done });

            let parsed: { frame: string; rest: string } | null;
            while ((parsed = takeSseEvent(buffer)) !== null) {
                buffer = parsed.rest;
                dispatchSseFrame(parsed.frame, onToken, onSources);
            }

            if (done) break;
        }
        onDone();
    } catch (err) {
        const e = err as Error;
        const msg = e?.message || "";
        if (msg.includes("ERR_INCOMPLETE_CHUNKED_ENCODING") || msg.includes("network error")) {
            onError(
                "The assistant connection was interrupted (often a proxy timeout or server restart). Check backend logs for RAG chat errors.",
            );
        } else {
            onError(msg || GENERIC_CHAT_ERROR);
        }
    }
}

const fetchConversations = async (): Promise<ConversationSummary[]> => {
    try {
        const res = await apiClient(`${API_BASE}/conversations`, { credentials: "include" });
        if (!res.ok) return [];
        return (await res.json()) as ConversationSummary[];
    } catch {
        return [];
    }
};

const fetchConversationMessages = async (sid: string): Promise<PersistedMessage[]> => {
    try {
        const res = await apiClient(`${API_BASE}/conversations/${sid}`, { credentials: "include" });
        if (!res.ok) return [];
        return (await res.json()) as PersistedMessage[];
    } catch {
        return [];
    }
};

const deleteConversationRequest = (sid: string): Promise<Response> =>
    apiClient(`${API_BASE}/conversations/${sid}`, { method: "DELETE", credentials: "include" });

function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async (): Promise<void> => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <button onClick={copy} className="copy-btn" title={label}>
            {copied ? <CheckIcon /> : <CopyIcon />}
            <span>{copied ? "Copied" : label}</span>
        </button>
    );
}

function Message({
    msg,
    isStreaming = false,
    canAddColleges = false,
    athleteUsername = "",
    showCollegeActions = false,
    collegeHint = "",
}: {
    msg: ChatMessage;
    isStreaming?: boolean;
    canAddColleges?: boolean;
    athleteUsername?: string;
    showCollegeActions?: boolean;
    collegeHint?: string;
}) {
    const isUser = msg.role === "user";
    return (
        <div className={`msg-row ${isUser ? "user" : "assistant"}`}>
            <div className="msg-avatar">
                {isUser ? <UserIcon /> : <img src={PolarisAssistIcon} alt="" className="msg-avatar-logo" />}
            </div>
            <div className="msg-content">
                <div className="msg-author">{isUser ? "You" : "Polaris Assist"}</div>
                <div className={`msg-bubble${isStreaming ? " typing" : ""}`}>
                    {isUser ? (
                        <p>{msg.content}</p>
                    ) : isStreaming ? (
                        <>
                            <span />
                            <span />
                            <span />
                        </>
                    ) : (
                        <AssistAnswerBody
                            markdown={msg.content}
                            canAdd={canAddColleges && showCollegeActions}
                            username={athleteUsername}
                            collegeHint={collegeHint}
                            userQuery={collegeHint}
                        />
                    )}
                </div>
                {!isUser && !isStreaming && <AssistSourceList sources={msg.sources ?? []} />}
                {!isUser && msg.content && (
                    <div className="msg-actions">
                        <CopyButton text={msg.content} />
                    </div>
                )}
            </div>
        </div>
    );
}

const MOBILE_LAYOUT_MQ = "(max-width: 767px)";
const DESKTOP_SIDEBAR_MQ = "(min-width: 768px)";

function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(() =>
        typeof window !== "undefined" ? window.matchMedia(query).matches : false,
    );

    useEffect(() => {
        const media = window.matchMedia(query);
        const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
        setMatches(media.matches);
        media.addEventListener("change", onChange);
        return () => media.removeEventListener("change", onChange);
    }, [query]);

    return matches;
}

export default function RagChat() {
    const { userInfo } = useAuth();
    const { available: assistAvailable, visible: assistVisible } = useAssistAccess();
    const athleteUsername = userInfo?.userId ?? "";
    const canAddColleges =
        Boolean(athleteUsername) && (userInfo?.role ?? "").toUpperCase() === "ATHLETE";
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState<string>(() => uuidv4());
    const [conversations, setConversations] = useState<ConversationSummary[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [usage, setUsage] = useState<MyAssistUsage | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(
        () => typeof window !== "undefined" && window.matchMedia(DESKTOP_SIDEBAR_MQ).matches,
    );
    const isMobileLayout = useMediaQuery(MOBILE_LAYOUT_MQ);

    const bottomRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);

    const closeSidebarIfMobile = useCallback(() => {
        if (window.matchMedia(MOBILE_LAYOUT_MQ).matches) {
            setSidebarOpen(false);
        }
    }, []);

    useEffect(() => {
        const media = window.matchMedia(MOBILE_LAYOUT_MQ);
        const syncSidebar = (event: MediaQueryListEvent) => {
            setSidebarOpen(!event.matches);
        };
        media.addEventListener("change", syncSidebar);
        return () => media.removeEventListener("change", syncSidebar);
    }, []);

    const loadUsage = useCallback(async (): Promise<void> => {
        try {
            const next = await fetchMyAssistUsage();
            setUsage(next);
        } catch {
            // Keep the last known status so a transient load failure does not unlock send.
        }
    }, []);

    useEffect(() => {
        if (assistAvailable === false) {
            return;
        }
        fetchConversations().then(setConversations);
        void loadUsage();
        inputRef.current?.focus();
    }, [assistAvailable, loadUsage]);

    useEffect(() => {
        const onFocus = () => {
            void loadUsage();
        };
        window.addEventListener("focus", onFocus);
        const interval = window.setInterval(() => {
            void loadUsage();
        }, 60_000);
        return () => {
            window.removeEventListener("focus", onFocus);
            window.clearInterval(interval);
        };
    }, [loadUsage]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, loading]);

    const startNewChat = useCallback(() => {
        setSessionId(uuidv4());
        setMessages([]);
        setError(null);
        inputRef.current?.focus();
    }, []);

    const loadConversation = useCallback(async (sid: string): Promise<void> => {
        setSessionId(sid);
        const msgs = await fetchConversationMessages(sid);
        setMessages(
            msgs.map((m) => ({
                role: m.role,
                content: m.content,
                sources: parseAssistSources(m.sources),
            })),
        );
    }, []);

    const handleDelete = useCallback(
        async (e: React.MouseEvent, sid: string): Promise<void> => {
            e.stopPropagation();
            if (!window.confirm("Delete this conversation?")) return;
            await deleteConversationRequest(sid);
            setConversations((prev) => prev.filter((c) => c.sessionId !== sid));
            if (sid === sessionId) startNewChat();
        },
        [sessionId, startNewChat],
    );

    const copyFullConversation = (): void => {
        const text = messages
            .map((m) => `${m.role === "user" ? "You" : "Polaris Assist"}:\n${m.content}`)
            .join("\n\n---\n\n");
        navigator.clipboard.writeText(text);
    };

    const downloadConversation = (): void => {
        const text = messages
            .map((m) => `${m.role === "user" ? "You" : "Polaris Assist"}:\n${m.content}`)
            .join("\n\n---\n\n");
        const blob = new Blob([text], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `polaris-chat-${new Date().toISOString().split("T")[0]}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const maxUserMessageTokens = usage?.maxUserMessageTokens ?? 2000;
    const inputOverLimit = estimateTokens(input) > maxUserMessageTokens;
    const limitReached = usage != null && !usage.canSubmit;
    const composerDisabled = loading || limitReached;

    const sendMessage = useCallback(async (): Promise<void> => {
        const text = input.trim();
        if (!text || loading || limitReached) return;
        if (estimateTokens(text) > maxUserMessageTokens) {
            setError(MESSAGE_TOO_LONG);
            return;
        }

        setInput("");
        setError(null);
        setMessages((prev) => [...prev, { role: "user", content: text }]);
        setLoading(true);
        setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

        await streamChat(
            text,
            sessionId,
            (token) => {
                setMessages((prev) => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    updated[updated.length - 1] = {
                        ...last,
                        content: last.content + token,
                    };
                    return updated;
                });
            },
            () => {
                setLoading(false);
                fetchConversations().then(setConversations);
                void loadUsage();
            },
            (errMsg) => {
                setLoading(false);
                setError(errMsg);
                setMessages((prev) => prev.slice(0, -1));
                void loadUsage();
            },
            (sources) => {
                setMessages((prev) => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (!last || last.role !== "assistant") {
                        return prev;
                    }
                    updated[updated.length - 1] = { ...last, sources };
                    return updated;
                });
            },
        );
    }, [input, limitReached, loadUsage, loading, maxUserMessageTokens, sessionId]);

    const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void sendMessage();
        }
    };

    const groupedConvos = conversations.reduce<Record<string, ConversationSummary[]>>(
        (groups, c) => {
            const date = new Date(c.updatedAt);
            const now = new Date();
            const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
            let group: string;
            if (diffDays === 0) group = "Today";
            else if (diffDays === 1) group = "Yesterday";
            else if (diffDays < 7) group = "This Week";
            else if (diffDays < 30) group = "This Month";
            else group = "Older";
            groups[group] = groups[group] || [];
            groups[group].push(c);
            return groups;
        },
        {},
    );

    const groupOrder = ["Today", "Yesterday", "This Week", "This Month", "Older"] as const;

    if (!assistVisible) {
        return (
            <div
                className="rag-chat-page"
                data-testid={assistAvailable === false ? "polaris-assist-unavailable" : "polaris-assist-checking"}
            >
                <main className="chat-main">
                    <header className="chat-topbar">
                        <div className="topbar-brand">
                            <img src={PolarisAssistIcon} alt="" className="topbar-brand-icon" />
                            <div className="topbar-brand-title">
                                <h2>Polaris Assist</h2>
                            </div>
                        </div>
                    </header>
                    {assistAvailable === false ? (
                        <div className="chat-scroll">
                            <div className="rag-assist-unavailable">
                                <p>Polaris Assist is not available for your organisation.</p>
                            </div>
                        </div>
                    ) : null}
                </main>
            </div>
        );
    }

    return (
        <div className={`rag-chat-page${sidebarOpen && isMobileLayout ? " rag-chat-page--sidebar-open" : ""}`}>
            {isMobileLayout && sidebarOpen && (
                <button
                    type="button"
                    className="rag-chat-sidebar-backdrop"
                    aria-label="Close conversations"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <ChatHistorySidebar
                open={sidebarOpen}
                sessionId={sessionId}
                conversations={conversations}
                groupedConvos={groupedConvos}
                groupOrder={groupOrder}
                onNewChat={() => {
                    startNewChat();
                    closeSidebarIfMobile();
                }}
                onSelect={(sid) => {
                    void loadConversation(sid);
                    closeSidebarIfMobile();
                }}
                onDelete={(event, sid) => void handleDelete(event, sid)}
            />

            <main className="chat-main">
                <header className="chat-topbar">
                    <button
                        className="icon-btn"
                        onClick={() => setSidebarOpen((s) => !s)}
                        title="Toggle sidebar"
                    >
                        <MenuIcon />
                    </button>
                    <div className="topbar-brand">
                        <img src={PolarisAssistIcon} alt="" className="topbar-brand-icon" />
                        <div className="topbar-brand-title">
                            <h2>Polaris Assist</h2>
                            <span className="topbar-beta-badge">Beta</span>
                        </div>
                    </div>
                    <div className="topbar-actions">
                        {messages.length > 0 && (
                            <>
                                <button
                                    className="icon-btn"
                                    onClick={copyFullConversation}
                                    title="Copy conversation"
                                >
                                    <CopyIcon />
                                </button>
                                <button
                                    className="icon-btn"
                                    onClick={downloadConversation}
                                    title="Download conversation"
                                >
                                    <DownloadIcon />
                                </button>
                            </>
                        )}
                    </div>
                </header>

                <div className="chat-scroll">
                    <div className={`chat-content${messages.length === 0 ? " chat-content--empty" : ""}`}>
                        {messages.length === 0 && (
                            <div className="welcome-screen">
                                <p>
                                    This chat is backed by a founding team of industry leaders and experts who
                                    contributed their expertise in education, recruiting, and NIL to help build
                                    and shape this experience.
                                </p>
                            </div>
                        )}

                        {messages.map((msg, i) => (
                            <Message
                                key={i}
                                msg={msg}
                                isStreaming={
                                    loading &&
                                    i === messages.length - 1 &&
                                    msg.role === "assistant" &&
                                    msg.content === ""
                                }
                                canAddColleges={canAddColleges}
                                athleteUsername={athleteUsername}
                                showCollegeActions={!loading}
                                collegeHint={priorUserContent(messages, i)}
                            />
                        ))}
                        <div ref={bottomRef} />
                    </div>
                </div>

                {(limitReached && usage?.message) || error || (inputOverLimit && !limitReached) ? (
                    <div className="chat-alerts" data-testid="chat-alerts">
                        {limitReached && usage?.message && (
                            <div className="chat-error-banner" role="status">
                                <AlertIcon /> {usage.message}
                            </div>
                        )}
                        {error && (
                            <div className="chat-error-banner">
                                <AlertIcon /> {error}
                            </div>
                        )}
                        {inputOverLimit && !limitReached && (
                            <div className="chat-error-banner" role="status">
                                <AlertIcon /> {MESSAGE_TOO_LONG}
                            </div>
                        )}
                    </div>
                ) : null}

                <div className="chat-input-wrap">
                    {usage ? (
                        <p className="chat-token-usage" aria-live="polite">
                            {formatAssistTokenUsage(
                                usage.dailyUsedTokens ?? 0,
                                usage.remainingDailyTokens,
                            )}
                        </p>
                    ) : null}
                    <div className="chat-input-inner">
                        <textarea
                            ref={inputRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKey}
                            placeholder="Let's get started!"
                            rows={1}
                            maxLength={SOFT_MAX_CHARS}
                            disabled={composerDisabled}
                        />
                        <button
                            type="button"
                            className="send-btn"
                            aria-label="Send message"
                            onClick={() => void sendMessage()}
                            disabled={!input.trim() || composerDisabled || inputOverLimit}
                        >
                            <SendIcon />
                        </button>
                    </div>
                    <p className="chat-disclaimer">
                        Polaris Assist does not give medical or legal advice. It uses official U.S. sources
                        (policy documents, College Scorecard, cited NCAA/ED sites) and does not rank
                        programs or judge campus social life. Data can lag — always verify with a
                        counselor, compliance office, or the school.
                    </p>
                </div>
            </main>
        </div>
    );
}

const MenuIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
);
const CopyIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
);
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const DownloadIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);
const SendIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);
const UserIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);
const AlertIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);
